import { useState } from "react";
import { Grid } from "@mui/material"
import { Button } from "../../components/shared/FormElements";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faRefresh } from "@fortawesome/free-solid-svg-icons";
import { useAppDispatch } from '../../hooks';
import { useAppContext } from '../../Context';

import { setPopAlert } from "../../slices/miscSlice";
import { setLoading } from "../../slices/miscSlice";
import { CustomerDuplicateType } from "../../types";
import { post } from "../../agent";
import { usePermissions } from "../../services";

type ViewCustomersProps = {
    siigoConfigured: boolean;
    alegraConfigured: boolean;
};

const ViewCustomers: React.FC<ViewCustomersProps> = ({ siigoConfigured, alegraConfigured }) => {
    const dispatch = useAppDispatch();
    const { apiCall } = useAppContext();
    const { canView_customer, canAdd_customer } = usePermissions(['view_customer', 'add_customer']);
    const [duplicates, setDuplicates] = useState<Array<CustomerDuplicateType>>([]);
    const [showDuplicates, setShowDuplicates] = useState(false);
    const [selectedDuplicateKeys, setSelectedDuplicateKeys] = useState<Record<string, boolean>>({});
    const FIELD_LABELS: Record<string, string> = {
        customer_name: 'Nombre',
        identification: 'Identificación',
        email: 'Correo',
        phone: 'Teléfono/Celular',
    };
    const SOURCE_LABELS: Record<string, string> = {
        siigo: 'Siigo',
        alegra: 'Alegra',
        manual: 'Manual',
        mixed: 'Mixto',
    };

    const resolveAlegraDuplicate = async (payload: {
        customer_id: number;
        alegra_id: string | number;
        updates?: Record<string, any>;
    }) => {
        try {
            const rawResp = await post('customers/resolve-alegra-duplicate/', {
                ...payload,
                updates: payload.updates || {},
            });
            let data: any = null;
            try {
                data = await rawResp.json();
            } catch (error) {
                data = null;
            }
            const ok = rawResp.status === 200 || rawResp.status === 201;
            return {
                ok,
                message: data?.detail,
                data,
            };
        } catch (error) {
            return {
                ok: false,
                message: 'No se pudo actualizar el cliente',
                data: null,
            };
        }
    };

    const handleRefreshClients = async () => {
        dispatch(setLoading(true));
        const response = await apiCall('GET', 'customers/refresh/', null, '', 'Error al obtener los clientes');
        dispatch(setLoading(false));
        if (response) {
            dispatch(setPopAlert({
                alertType: 'success',
                message: 'Clientes actualizados correctamente',
                show: true
            }));
        }else{
            dispatch(setPopAlert({
                alertType: 'error',
                message: 'Error al actualizar los clientes',
                show: true
            }));
        }
    }

    const handleRefreshAlegra = async () => {
        dispatch(setLoading(true));
        const response = await apiCall(
            'GET',
            'customers/refresh-alegra/',
            { all: true },
            '',
            'Error al obtener los clientes'
        );
        dispatch(setLoading(false));
        if (response) {
            dispatch(setPopAlert({
                alertType: 'success',
                message: `Alegra: ${response.created || 0} importados, ${response.updated || 0} actualizados, ${response.fetched || response.count_in_page || 0} en total`,
                show: true
            }));
            if (response.duplicates?.length) {
                setDuplicates(response.duplicates);
                setShowDuplicates(true);
                setSelectedDuplicateKeys({});
            }
        }else{
            dispatch(setPopAlert({
                alertType: 'error',
                message: 'Error al actualizar los clientes desde Alegra',
                show: true
            }));
        }
    }

    const handleResolveDuplicate = async (dup: CustomerDuplicateType) => {
        if (!dup.can_update || !dup.suggested_updates) {
            return;
        }
        const confirmed = window.confirm('Deseas actualizar la informacion del cliente con los datos de Alegra?');
        if (!confirmed) {
            return;
        }
        dispatch(setLoading(true));
        const response = await resolveAlegraDuplicate({
            customer_id: dup.siigo_customer.id,
            alegra_id: dup.alegra_customer.id,
            updates: dup.suggested_updates || {},
        });
        dispatch(setLoading(false));
        if (response.ok) {
            dispatch(setPopAlert({
                alertType: 'success',
                message: 'Cliente actualizado con datos de Alegra',
                show: true
            }));
            const resolvedKey = `${dup.siigo_customer.id}-${dup.alegra_customer.id}`;
            setDuplicates((prev) =>
                prev.map((item) => {
                    const key = `${item.siigo_customer.id}-${item.alegra_customer.id}`;
                    if (key !== resolvedKey) {
                        return item;
                    }
                    return mergeDupSources(item, 'alegra');
                })
            );
            setSelectedDuplicateKeys((prev) => ({ ...prev, [resolvedKey]: false }));
        } else {
            dispatch(setPopAlert({
                alertType: 'warning',
                message: response.message || 'No se pudo actualizar el cliente',
                show: true
            }));
        }
    }

    const resolveFieldSource = (dup: CustomerDuplicateType, field: string) => {
        const sources = dup.siigo_customer.sync_sources || {};
        if (sources[field]) {
            return sources[field];
        }
        if (!dup.siigo_customer.siigo_id) {
            return 'manual';
        }
        return 'siigo';
    };

    const getDupStatus = (dup: CustomerDuplicateType) => {
        const fields = Object.keys(dup.differences || {});
        const sources = fields.map((field) => resolveFieldSource(dup, field)).filter(Boolean);
        if (!sources.length) {
            return resolveFieldSource(dup, 'customer_name');
        }
        if (sources.every((source) => source === sources[0])) {
            return sources[0];
        }
        return 'mixed';
    };

    const mergeDupSources = (dup: CustomerDuplicateType, source: string) => {
        const fields = dup.suggested_fields || Object.keys(dup.suggested_updates || {});
        const nextSources = { ...(dup.siigo_customer.sync_sources || {}) };
        fields.forEach((field) => {
            nextSources[field] = source;
        });
        return {
            ...dup,
            siigo_customer: {
                ...dup.siigo_customer,
                sync_sources: nextSources,
            },
        };
    };

    const toggleDuplicateSelection = (key: string, checked: boolean) => {
        setSelectedDuplicateKeys((prev) => ({ ...prev, [key]: checked }));
    };

    const toggleSelectAllDuplicates = (checked: boolean) => {
        const next: Record<string, boolean> = {};
        duplicates.forEach((dup) => {
            const key = `${dup.siigo_customer.id}-${dup.alegra_customer.id}`;
            const status = getDupStatus(dup);
            if (dup.can_update && status !== 'alegra') {
                next[key] = checked;
            }
        });
        setSelectedDuplicateKeys(next);
    };

    const handleResolveSelectedDuplicates = async () => {
        const selected = duplicates.filter((dup) => {
            const key = `${dup.siigo_customer.id}-${dup.alegra_customer.id}`;
            const status = getDupStatus(dup);
            return dup.can_update && status !== 'alegra' && selectedDuplicateKeys[key];
        });
        if (!selected.length) {
            return;
        }
        const confirmed = window.confirm('Deseas actualizar los clientes seleccionados con los datos de Alegra?');
        if (!confirmed) {
            return;
        }
        dispatch(setLoading(true));
        let updatedCount = 0;
        let errorCount = 0;
        for (const dup of selected) {
            const response = await resolveAlegraDuplicate({
                customer_id: dup.siigo_customer.id,
                alegra_id: dup.alegra_customer.id,
                updates: dup.suggested_updates || {},
            });
            if (response.ok) {
                updatedCount += 1;
            } else {
                errorCount += 1;
                dispatch(setPopAlert({
                    alertType: 'warning',
                    message: response.message || 'No se pudo actualizar el cliente',
                    show: true
                }));
            }
        }
        dispatch(setLoading(false));
        if (updatedCount) {
            dispatch(setPopAlert({
                alertType: 'success',
                message: `Clientes actualizados: ${updatedCount}${errorCount ? ` (conflictos: ${errorCount})` : ''}`,
                show: true
            }));
            const resolvedKeys = new Set(selected.map((dup) => `${dup.siigo_customer.id}-${dup.alegra_customer.id}`));
            setDuplicates((prev) =>
                prev.map((dup) => {
                    const key = `${dup.siigo_customer.id}-${dup.alegra_customer.id}`;
                    if (!resolvedKeys.has(key)) {
                        return dup;
                    }
                    return mergeDupSources(dup, 'alegra');
                })
            );
            setSelectedDuplicateKeys((prev) => {
                const next = { ...prev };
                resolvedKeys.forEach((key) => {
                    next[key] = false;
                });
                return next;
            });
        } else {
            dispatch(setPopAlert({
                alertType: 'warning',
                message: errorCount ? `No se actualizo ningun cliente (conflictos: ${errorCount})` : 'No se actualizo ningun cliente',
                show: true
            }));
        }
    };

    const selectedDuplicatesCount = duplicates.reduce((count, dup) => {
        const key = `${dup.siigo_customer.id}-${dup.alegra_customer.id}`;
        const status = getDupStatus(dup);
        if (dup.can_update && status !== 'alegra' && selectedDuplicateKeys[key]) {
            return count + 1;
        }
        return count;
    }, 0);

    const allResolved =
        duplicates.length > 0 &&
        duplicates.every((dup) => {
            return getDupStatus(dup) === 'alegra';
        });

    if (!canView_customer) {
        return null;
    }

    return <>
         <h2>Lista de Clientes</h2>
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={3} className="text-right">

                            </Grid>
                            <Grid item xs={12} sm={6} className="text-center">

                            {siigoConfigured && canAdd_customer && (
                                <Button onClick={handleRefreshClients}>
                                    <FontAwesomeIcon icon={faRefresh} spin /> Actualizar Clientes desde Siigo
                                </Button>
                            )}
                            {alegraConfigured && canAdd_customer && (
                                <Button onClick={handleRefreshAlegra}>
                                    <FontAwesomeIcon icon={faRefresh} spin /> Actualizar Clientes desde Alegra
                                </Button>
                            )}
                            </Grid>
                            <Grid item xs={12} sm={3} className="text-right"></Grid>
                        </Grid>
        {showDuplicates && (
            <div className="mt-3">
                <h4>Clientes duplicados con Siigo</h4>
                {duplicates.length === 0 && <div>No se detectaron duplicados.</div>}
                {allResolved && <div>Todos los duplicados fueron resueltos.</div>}
                {duplicates.length > 0 && (
                    <div className="mb-2">
                        <label style={{marginBottom:30, textAlign:'center'}}>
                            <input
                                type='checkbox'
                                checked={
                                    duplicates.filter((dup) => dup.can_update && getDupStatus(dup) !== 'alegra').length > 0 &&
                                    duplicates.filter((dup) => dup.can_update && getDupStatus(dup) !== 'alegra').every((dup) => {
                                        const key = `${dup.siigo_customer.id}-${dup.alegra_customer.id}`;
                                        return !!selectedDuplicateKeys[key];
                                    })
                                }
                                onChange={(event) => toggleSelectAllDuplicates(event.target.checked)}
                            />
                            Seleccionar todos
                        </label>
                        {canAdd_customer && (
                            <Button onClick={handleResolveSelectedDuplicates} disabled={selectedDuplicatesCount === 0}>
                                Actualizar seleccionados ({selectedDuplicatesCount})
                            </Button>
                        )}
                    </div>
                )}
                {duplicates.map((dup) => (
                    <div
                        key={`${dup.siigo_customer.id}-${dup.alegra_customer.id}`}
                        style={{
                            border: '1px solid #e5e7eb',
                            borderRadius: '8px',
                            padding: '12px',
                            marginBottom: '12px',
                            background: '#fff',
                            boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
                        }}
                    >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            {dup.can_update && getDupStatus(dup) !== 'alegra' && (
                                <label style={{ marginRight: '4px' }}>
                                    <input
                                        type='checkbox'
                                        checked={!!selectedDuplicateKeys[`${dup.siigo_customer.id}-${dup.alegra_customer.id}`]}
                                        onChange={(event) =>
                                            toggleDuplicateSelection(`${dup.siigo_customer.id}-${dup.alegra_customer.id}`, event.target.checked)
                                        }
                                    />
                                </label>
                            )}
                            <strong>{dup.siigo_customer.customer_name}</strong>
                        </div>
                        <div style={{ marginTop: '6px', fontWeight: 600 }}>
                            {getDupStatus(dup) === 'alegra' && 'Actualmente con los datos de Alegra'}
                            {getDupStatus(dup) === 'siigo' && 'Actualmente con los datos de Siigo'}
                            {getDupStatus(dup) === 'manual' && 'Actualmente con los datos Manuales'}
                            {getDupStatus(dup) === 'mixed' && 'Actualmente con datos mixtos'}
                        </div>
                        <div style={{ marginTop: '4px' }}>
                            Siigo ID: {dup.siigo_customer.siigo_id || '-'} | Alegra ID: {dup.alegra_customer.id}
                        </div>
                        {dup.conflict && (
                            <div style={{ color: '#b45309' }}>
                                Conflicto: Alegra ID ya asociado, es posible que este cliente esté duplicado en la plataforma de alegra, no es error de Jarvis
                            </div>
                        )}
                        {dup.match_reason === 'name' && (
                            <div style={{ color: '#2563eb' }}>Coincidencia por nombre (sin identificación)</div>
                        )}
                        {Object.keys(dup.differences || {}).length > 0 && (
                            <div style={{ marginTop: '8px' }}>
                                <strong>Cambios detectados</strong>
                                {Object.entries(dup.differences).map(([field, diff]) => (
                                    <div key={field}>
                                        {FIELD_LABELS[field] || 'Campo'}: {diff.current || '-'} {'>'} {diff.updated || '-'} (
                                            {SOURCE_LABELS[resolveFieldSource(dup, field)] || 'Origen desconocido'}
                                        )
                                    </div>
                                ))}
                            </div>
                        )}
                        {dup.can_update && getDupStatus(dup) !== 'alegra' && canAdd_customer && (
                            <Button onClick={() => handleResolveDuplicate(dup)}>Actualizar con Alegra</Button>
                        )}
                    </div>
                ))}
                <Button onClick={() => setShowDuplicates(false)}>Cerrar</Button>
            </div>
        )}
    </>
}

export default ViewCustomers
